import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, LogOut, Image, Type, Share2, Plus, Trash2, Save, Loader2, CheckCircle, Eye, FolderOpen, Award, BarChart3, User, Pencil, X } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = '';

const AdminPage = () => {
  const { themeColor } = useTheme();
  const [token, setToken] = useState(sessionStorage.getItem('admin_token') || '');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('personal');
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');

  // Data states
  const [personalInfo, setPersonalInfo] = useState({});
  const [socials, setSocials] = useState({ instagram: '', youtube: '', spotify: '', twitter: '', github: '' });
  const [gallery, setGallery] = useState([]);
  const [projects, setProjects] = useState([]);
  const [skills, setSkills] = useState([]);
  const [experiences, setExperiences] = useState([]);

  // Form states
  const [newPhoto, setNewPhoto] = useState({ title: '', image: '', category: '' });
  const [newProject, setNewProject] = useState({ title: '', description: '', image: '', liveUrl: '', category: '', tags: '', year: new Date().getFullYear(), featured: false });
  const [newSkill, setNewSkill] = useState({ name: '', percentage: 80 });
  const [newExp, setNewExp] = useState({ title: '', company: '', period: '', description: '', details: '' });
  const [editingProject, setEditingProject] = useState(null);
  const [editingExp, setEditingExp] = useState(null);

  const authHeaders = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  const showSaved = () => { setSaveStatus('saved'); setTimeout(() => setSaveStatus(''), 2000); };

  useEffect(() => {
    if (token) {
      fetch(`${BACKEND_URL}/api/admin/verify`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => { if (r.ok) setIsAuthenticated(true); else { setToken(''); sessionStorage.removeItem('admin_token'); } })
        .catch(() => { setToken(''); sessionStorage.removeItem('admin_token'); });
    }
  }, [token]);

  const loadData = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    const h = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    try {
      const [piRes, socRes, galRes, projRes, skRes, expRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/personal-info`, { headers: h }),
        fetch(`${BACKEND_URL}/api/admin/socials`, { headers: h }),
        fetch(`${BACKEND_URL}/api/admin/gallery`, { headers: h }),
        fetch(`${BACKEND_URL}/api/admin/projects`, { headers: h }),
        fetch(`${BACKEND_URL}/api/admin/skills`, { headers: h }),
        fetch(`${BACKEND_URL}/api/admin/experiences`, { headers: h }),
      ]);
      if (piRes.ok) { const d = await piRes.json(); setPersonalInfo(d.personalInfo || {}); }
      if (socRes.ok) { const d = await socRes.json(); if (d.socials) setSocials(prev => ({ ...prev, ...d.socials })); }
      if (galRes.ok) { const d = await galRes.json(); setGallery(d.gallery || []); }
      if (projRes.ok) { const d = await projRes.json(); setProjects(d.projects || []); }
      if (skRes.ok) { const d = await skRes.json(); setSkills(d.skills || []); }
      if (expRes.ok) { const d = await expRes.json(); setExperiences(d.experiences || []); }
    } catch (_) { /* network error */ }
    setLoading(false);
  }, [isAuthenticated, token]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password }) });
      if (res.ok) { const d = await res.json(); setToken(d.token); sessionStorage.setItem('admin_token', d.token); setIsAuthenticated(true); }
      else setLoginError('Yanlış şifre');
    } catch { setLoginError('Bağlantı hatası'); }
  };

  const handleLogout = () => { setToken(''); setIsAuthenticated(false); sessionStorage.removeItem('admin_token'); };

  // ── Actions ──
  const savePersonalInfo = async () => {
    const res = await fetch(`${BACKEND_URL}/api/admin/personal-info`, { method: 'PUT', headers: authHeaders, body: JSON.stringify(personalInfo) });
    if (res.ok) showSaved();
  };
  const saveSocials = async () => {
    const res = await fetch(`${BACKEND_URL}/api/admin/socials`, { method: 'PUT', headers: authHeaders, body: JSON.stringify(socials) });
    if (res.ok) showSaved();
  };
  const addPhoto = async () => {
    if (!newPhoto.title || !newPhoto.image) return;
    const res = await fetch(`${BACKEND_URL}/api/admin/gallery`, { method: 'POST', headers: authHeaders, body: JSON.stringify(newPhoto) });
    if (res.ok) { const d = await res.json(); setGallery([...gallery, d.item]); setNewPhoto({ title: '', image: '', category: '' }); showSaved(); }
  };
  const deletePhoto = async (id) => {
    const res = await fetch(`${BACKEND_URL}/api/admin/gallery/${id}`, { method: 'DELETE', headers: authHeaders });
    if (res.ok) { setGallery(gallery.filter(g => g.id !== id)); showSaved(); }
  };
  const addProject = async () => {
    if (!newProject.title || !newProject.description) return;
    const payload = { ...newProject, tags: newProject.tags.split(',').map(t => t.trim()).filter(Boolean), year: parseInt(newProject.year) || new Date().getFullYear() };
    const res = await fetch(`${BACKEND_URL}/api/admin/projects`, { method: 'POST', headers: authHeaders, body: JSON.stringify(payload) });
    if (res.ok) { const d = await res.json(); setProjects([d.item, ...projects]); setNewProject({ title: '', description: '', image: '', liveUrl: '', category: '', tags: '', year: new Date().getFullYear(), featured: false }); showSaved(); }
  };
  const updateProject = async (id) => {
    if (!editingProject) return;
    const payload = { ...editingProject, tags: typeof editingProject.tags === 'string' ? editingProject.tags.split(',').map(t => t.trim()).filter(Boolean) : editingProject.tags };
    const res = await fetch(`${BACKEND_URL}/api/admin/projects/${id}`, { method: 'PUT', headers: authHeaders, body: JSON.stringify(payload) });
    if (res.ok) { setProjects(projects.map(p => p.id === id ? { ...p, ...payload } : p)); setEditingProject(null); showSaved(); }
  };
  const deleteProject = async (id) => {
    const res = await fetch(`${BACKEND_URL}/api/admin/projects/${id}`, { method: 'DELETE', headers: authHeaders });
    if (res.ok) { setProjects(projects.filter(p => p.id !== id)); showSaved(); }
  };
  const addSkill = async () => {
    if (!newSkill.name) return;
    const res = await fetch(`${BACKEND_URL}/api/admin/skills`, { method: 'POST', headers: authHeaders, body: JSON.stringify(newSkill) });
    if (res.ok) { const d = await res.json(); setSkills([...skills, d.item]); setNewSkill({ name: '', percentage: 80 }); showSaved(); }
  };
  const deleteSkill = async (id) => {
    const res = await fetch(`${BACKEND_URL}/api/admin/skills/${id}`, { method: 'DELETE', headers: authHeaders });
    if (res.ok) { setSkills(skills.filter(s => s.id !== id)); showSaved(); }
  };
  const addExperience = async () => {
    if (!newExp.title) return;
    const payload = { ...newExp, details: newExp.details.split('\n').map(d => d.trim()).filter(Boolean) };
    const res = await fetch(`${BACKEND_URL}/api/admin/experiences`, { method: 'POST', headers: authHeaders, body: JSON.stringify(payload) });
    if (res.ok) { const d = await res.json(); setExperiences([...experiences, d.item]); setNewExp({ title: '', company: '', period: '', description: '', details: '' }); showSaved(); }
  };
  const deleteExperience = async (id) => {
    const res = await fetch(`${BACKEND_URL}/api/admin/experiences/${id}`, { method: 'DELETE', headers: authHeaders });
    if (res.ok) { setExperiences(experiences.filter(e => e.id !== id)); showSaved(); }
  };

  const tabs = [
    { id: 'personal', label: 'Kişisel Bilgiler', icon: User },
    { id: 'socials', label: 'Sosyal Medya', icon: Share2 },
    { id: 'projects', label: 'Projeler', icon: FolderOpen },
    { id: 'gallery', label: 'Galeri', icon: Image },
    { id: 'skills', label: 'Yetenekler', icon: BarChart3 },
    { id: 'experiences', label: 'Deneyimler', icon: Award },
  ];

  const inputCls = "w-full px-4 py-3 bg-zinc-800/50 border border-zinc-700/50 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-600";
  const cardCls = "p-6 rounded-2xl bg-zinc-900/70 border border-zinc-800/50";
  const BtnSave = ({ onClick, label = "Kaydet" }) => (
    <button onClick={onClick} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-black" style={{ backgroundColor: themeColor.primary }} data-testid="admin-save-btn">
      <Save size={16} /> {label}
    </button>
  );
  const BtnAdd = ({ onClick, label = "Ekle" }) => (
    <button onClick={onClick} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-black" style={{ backgroundColor: themeColor.primary }} data-testid="admin-add-btn">
      <Plus size={16} /> {label}
    </button>
  );

  // ── LOGIN ──
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#050505' }}>
        <motion.form onSubmit={handleLogin} className="w-full max-w-sm p-8 rounded-2xl bg-zinc-900/80 border border-zinc-800/50 backdrop-blur-xl" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-center mb-6">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `rgba(${themeColor.rgb}, 0.1)` }}><Lock size={24} style={{ color: themeColor.primary }} /></div>
          </div>
          <h2 className="text-xl font-bold text-white text-center mb-2">Admin Paneli</h2>
          <p className="text-sm text-zinc-500 text-center mb-6">Devam etmek için şifrenizi girin</p>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Şifre" className={`${inputCls} mb-4`} data-testid="admin-password-input" />
          {loginError && <p className="text-red-400 text-sm mb-3 text-center">{loginError}</p>}
          <button type="submit" className="w-full py-3 rounded-xl font-medium text-black transition-all hover:opacity-90" style={{ backgroundColor: themeColor.primary }} data-testid="admin-login-btn">Giriş Yap</button>
        </motion.form>
      </div>
    );
  }

  // ── ADMIN PANEL ──
  return (
    <div className="min-h-screen pt-6 pb-16 px-4" style={{ background: '#050505' }}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <span className="text-2xl" style={{ color: themeColor.primary }}>✶</span>
            <div><h1 className="text-xl font-bold text-white">Admin Paneli</h1><p className="text-sm text-zinc-500">Site yönetimi</p></div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-4 py-2 rounded-xl bg-zinc-800/50 border border-zinc-800/50 text-zinc-400 hover:text-white transition-all text-sm"><Eye size={16} /> Siteyi Gör</a>
            <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:text-red-300 transition-all text-sm" data-testid="admin-logout-btn"><LogOut size={16} /> Çıkış</button>
          </div>
        </div>

        {/* Save indicator */}
        <AnimatePresence>
          {saveStatus === 'saved' && (
            <motion.div className="fixed top-6 right-6 z-50 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-green-500/10 border border-green-500/20 text-green-400" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <CheckCircle size={16} /> Kaydedildi
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${activeTab === tab.id ? 'text-black' : 'text-zinc-400 bg-zinc-900/50 border border-zinc-800/50 hover:text-white'}`}
              style={activeTab === tab.id ? { backgroundColor: themeColor.primary } : {}}
              data-testid={`admin-tab-${tab.id}`}>
              <tab.icon size={16} /> {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 size={32} className="animate-spin" style={{ color: themeColor.primary }} /></div>
        ) : (
          <>
            {/* ── PERSONAL INFO TAB ── */}
            {activeTab === 'personal' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className={cardCls}>
                  <h3 className="text-lg font-semibold text-white mb-4">Kişisel Bilgiler</h3>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    {[
                      { key: 'name', label: 'Kullanıcı Adı' }, { key: 'fullName', label: 'Tam Ad' },
                      { key: 'role', label: 'Ünvan' }, { key: 'email', label: 'E-posta' },
                      { key: 'location', label: 'Konum' }, { key: 'avatar', label: 'Avatar URL' },
                      { key: 'musicSite', label: 'Müzik Sitesi URL' }, { key: 'github', label: 'GitHub URL' },
                      { key: 'birthYear', label: 'Doğum Yılı' }, { key: 'experienceStart', label: 'Deneyim Başlangıç Yılı' },
                    ].map(field => (
                      <div key={field.key}>
                        <label className="block text-sm text-zinc-400 mb-1.5">{field.label}</label>
                        <input value={personalInfo[field.key] || ''} onChange={e => setPersonalInfo({ ...personalInfo, [field.key]: e.target.value })} className={inputCls} placeholder={field.label} data-testid={`personal-${field.key}`} />
                      </div>
                    ))}
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm text-zinc-400 mb-1.5">Bio</label>
                    <textarea value={personalInfo.bio || ''} onChange={e => setPersonalInfo({ ...personalInfo, bio: e.target.value })} rows={3} className={`${inputCls} resize-none`} placeholder="Hakkımda metni" />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm text-zinc-400 mb-1.5">Kısa Bio</label>
                    <input value={personalInfo.shortBio || ''} onChange={e => setPersonalInfo({ ...personalInfo, shortBio: e.target.value })} className={inputCls} placeholder="Kısa tanıtım" />
                  </div>
                  <BtnSave onClick={savePersonalInfo} />
                </div>
              </motion.div>
            )}

            {/* ── SOCIALS TAB ── */}
            {activeTab === 'socials' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className={cardCls}>
                  <h3 className="text-lg font-semibold text-white mb-4">Sosyal Medya Linkleri</h3>
                  <div className="space-y-4">
                    {Object.keys(socials).map(key => (
                      <div key={key}>
                        <label className="block text-sm text-zinc-400 mb-1.5 capitalize">{key}</label>
                        <input value={socials[key] || ''} onChange={e => setSocials({ ...socials, [key]: e.target.value })} className={inputCls} placeholder={`${key} URL`} data-testid={`socials-input-${key}`} />
                      </div>
                    ))}
                    <BtnSave onClick={saveSocials} />
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── PROJECTS TAB ── */}
            {activeTab === 'projects' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className={cardCls}>
                  <h3 className="text-lg font-semibold text-white mb-4">Yeni Proje Ekle</h3>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <input value={newProject.title} onChange={e => setNewProject({ ...newProject, title: e.target.value })} className={inputCls} placeholder="Proje Adı *" data-testid="project-input-title" />
                    <input value={newProject.category} onChange={e => setNewProject({ ...newProject, category: e.target.value })} className={inputCls} placeholder="Kategori" />
                    <input value={newProject.image} onChange={e => setNewProject({ ...newProject, image: e.target.value })} className={inputCls} placeholder="Görsel URL" />
                    <input value={newProject.liveUrl} onChange={e => setNewProject({ ...newProject, liveUrl: e.target.value })} className={inputCls} placeholder="Canlı URL" />
                    <input value={newProject.tags} onChange={e => setNewProject({ ...newProject, tags: e.target.value })} className={inputCls} placeholder="Etiketler (virgülle ayır)" />
                    <input type="number" value={newProject.year} onChange={e => setNewProject({ ...newProject, year: e.target.value })} className={inputCls} placeholder="Yıl" />
                  </div>
                  <textarea value={newProject.description} onChange={e => setNewProject({ ...newProject, description: e.target.value })} rows={2} className={`${inputCls} resize-none mb-4`} placeholder="Açıklama *" data-testid="project-input-desc" />
                  <div className="flex items-center gap-4">
                    <BtnAdd onClick={addProject} />
                    <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
                      <input type="checkbox" checked={newProject.featured} onChange={e => setNewProject({ ...newProject, featured: e.target.checked })} className="accent-cyan-500" /> Öne Çıkan
                    </label>
                  </div>
                </div>

                {/* Edit modal */}
                <AnimatePresence>
                  {editingProject && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className={`${cardCls} border-2`} style={{ borderColor: themeColor.primary }}>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-white">Proje Düzenle</h3>
                        <button onClick={() => setEditingProject(null)} className="text-zinc-400 hover:text-white"><X size={20} /></button>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4 mb-4">
                        <input value={editingProject.title || ''} onChange={e => setEditingProject({ ...editingProject, title: e.target.value })} className={inputCls} placeholder="Proje Adı" />
                        <input value={editingProject.category || ''} onChange={e => setEditingProject({ ...editingProject, category: e.target.value })} className={inputCls} placeholder="Kategori" />
                        <input value={editingProject.image || ''} onChange={e => setEditingProject({ ...editingProject, image: e.target.value })} className={inputCls} placeholder="Görsel URL" />
                        <input value={editingProject.liveUrl || ''} onChange={e => setEditingProject({ ...editingProject, liveUrl: e.target.value })} className={inputCls} placeholder="Canlı URL" />
                        <input value={Array.isArray(editingProject.tags) ? editingProject.tags.join(', ') : editingProject.tags || ''} onChange={e => setEditingProject({ ...editingProject, tags: e.target.value })} className={inputCls} placeholder="Etiketler" />
                        <input type="number" value={editingProject.year || ''} onChange={e => setEditingProject({ ...editingProject, year: parseInt(e.target.value) || '' })} className={inputCls} placeholder="Yıl" />
                      </div>
                      <textarea value={editingProject.description || ''} onChange={e => setEditingProject({ ...editingProject, description: e.target.value })} rows={2} className={`${inputCls} resize-none mb-4`} />
                      <BtnSave onClick={() => updateProject(editingProject.id)} label="Güncelle" />
                    </motion.div>
                  )}
                </AnimatePresence>

                {projects.length === 0 ? (
                  <p className="text-center text-zinc-500 py-8">Henüz proje eklenmemiş.</p>
                ) : (
                  <div className="space-y-3">
                    {projects.map(p => (
                      <div key={p.id} className="flex items-center gap-4 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800/50">
                        {p.image && <img src={p.image} alt={p.title} className="w-16 h-12 rounded-lg object-cover flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{p.title}</p>
                          <p className="text-xs text-zinc-500">{p.category} {p.year ? `• ${p.year}` : ''} {p.featured ? '• Öne Çıkan' : ''}</p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <button onClick={() => setEditingProject({ ...p })} className="p-2 rounded-lg bg-zinc-800/50 text-zinc-400 hover:text-white" data-testid={`project-edit-${p.id}`}><Pencil size={16} /></button>
                          <button onClick={() => deleteProject(p.id)} className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:text-red-300" data-testid={`project-delete-${p.id}`}><Trash2 size={16} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* ── GALLERY TAB ── */}
            {activeTab === 'gallery' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className={cardCls}>
                  <h3 className="text-lg font-semibold text-white mb-4">Yeni Fotoğraf Ekle</h3>
                  <div className="grid sm:grid-cols-3 gap-4 mb-4">
                    <input value={newPhoto.title} onChange={e => setNewPhoto({ ...newPhoto, title: e.target.value })} placeholder="Başlık" className={inputCls} data-testid="gallery-input-title" />
                    <input value={newPhoto.image} onChange={e => setNewPhoto({ ...newPhoto, image: e.target.value })} placeholder="Resim URL'si" className={inputCls} data-testid="gallery-input-image" />
                    <input value={newPhoto.category} onChange={e => setNewPhoto({ ...newPhoto, category: e.target.value })} placeholder="Kategori" className={inputCls} data-testid="gallery-input-category" />
                  </div>
                  <BtnAdd onClick={addPhoto} />
                </div>
                {gallery.length === 0 ? (
                  <p className="text-center text-zinc-500 py-8">Henüz fotoğraf eklenmemiş.</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {gallery.map(item => (
                      <div key={item.id} className="relative group rounded-2xl overflow-hidden border border-zinc-800/50">
                        <img src={item.image} alt={item.title} className="w-full aspect-square object-cover" />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <button onClick={() => deletePhoto(item.id)} className="p-3 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500/30" data-testid={`gallery-delete-${item.id}`}><Trash2 size={20} /></button>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                          <p className="text-white text-sm font-medium">{item.title}</p>
                          <p className="text-xs text-zinc-400">{item.category}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* ── SKILLS TAB ── */}
            {activeTab === 'skills' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className={cardCls}>
                  <h3 className="text-lg font-semibold text-white mb-4">Yeni Yetenek Ekle</h3>
                  <div className="flex gap-4 mb-4 items-end">
                    <div className="flex-1">
                      <label className="block text-sm text-zinc-400 mb-1.5">Yetenek Adı</label>
                      <input value={newSkill.name} onChange={e => setNewSkill({ ...newSkill, name: e.target.value })} className={inputCls} placeholder="Örn: React" data-testid="skill-input-name" />
                    </div>
                    <div className="w-32">
                      <label className="block text-sm text-zinc-400 mb-1.5">Seviye %</label>
                      <input type="number" min="0" max="100" value={newSkill.percentage} onChange={e => setNewSkill({ ...newSkill, percentage: parseInt(e.target.value) || 0 })} className={inputCls} data-testid="skill-input-pct" />
                    </div>
                    <BtnAdd onClick={addSkill} />
                  </div>
                </div>
                {skills.length === 0 ? (
                  <p className="text-center text-zinc-500 py-8">Henüz yetenek eklenmemiş.</p>
                ) : (
                  <div className="space-y-3">
                    {skills.map(s => (
                      <div key={s.id} className="flex items-center gap-4 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800/50">
                        <div className="flex-1">
                          <div className="flex justify-between mb-1.5">
                            <span className="text-white font-medium">{s.name}</span>
                            <span className="text-sm" style={{ color: themeColor.primary }}>{s.percentage}%</span>
                          </div>
                          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${s.percentage}%`, backgroundColor: themeColor.primary }} />
                          </div>
                        </div>
                        <button onClick={() => deleteSkill(s.id)} className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:text-red-300 flex-shrink-0" data-testid={`skill-delete-${s.id}`}><Trash2 size={16} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* ── EXPERIENCES TAB ── */}
            {activeTab === 'experiences' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className={cardCls}>
                  <h3 className="text-lg font-semibold text-white mb-4">Yeni Deneyim Ekle</h3>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <input value={newExp.title} onChange={e => setNewExp({ ...newExp, title: e.target.value })} className={inputCls} placeholder="Pozisyon *" data-testid="exp-input-title" />
                    <input value={newExp.company} onChange={e => setNewExp({ ...newExp, company: e.target.value })} className={inputCls} placeholder="Şirket" />
                    <input value={newExp.period} onChange={e => setNewExp({ ...newExp, period: e.target.value })} className={inputCls} placeholder="Dönem (Örn: 2023 - Günümüz)" />
                  </div>
                  <textarea value={newExp.description} onChange={e => setNewExp({ ...newExp, description: e.target.value })} rows={2} className={`${inputCls} resize-none mb-4`} placeholder="Açıklama" data-testid="exp-input-desc" />
                  <textarea value={newExp.details} onChange={e => setNewExp({ ...newExp, details: e.target.value })} rows={3} className={`${inputCls} resize-none mb-4`} placeholder="Detaylar (her satır bir madde)" data-testid="exp-input-details" />
                  <BtnAdd onClick={addExperience} />
                </div>
                {experiences.length === 0 ? (
                  <p className="text-center text-zinc-500 py-8">Henüz deneyim eklenmemiş.</p>
                ) : (
                  <div className="space-y-3">
                    {experiences.map(exp => (
                      <div key={exp.id} className="p-4 rounded-xl bg-zinc-900/50 border border-zinc-800/50">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-white font-medium">{exp.title}</p>
                            <p className="text-sm text-zinc-500">{exp.company} • {exp.period}</p>
                            <p className="text-sm text-zinc-400 mt-1">{exp.description}</p>
                            {exp.details && exp.details.length > 0 && (
                              <ul className="mt-2 space-y-1">
                                {exp.details.map((d, i) => (
                                  <li key={i} className="text-xs text-zinc-500 flex items-start gap-1.5">
                                    <span className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: themeColor.primary }} />
                                    {d}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          <button onClick={() => deleteExperience(exp.id)} className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:text-red-300 flex-shrink-0" data-testid={`exp-delete-${exp.id}`}><Trash2 size={16} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
